import React, { useState, useEffect, useCallback, useRef } from "react";
import { observer } from "mobx-react";
import { debounce } from "lodash";
import { useJsApiLoader, GoogleMap, Marker } from "@react-google-maps/api";
import { navigate } from "@reach/router";
import { ENV, GOOGLE_MAPS_API_KEY } from "../../../../config";
import ClassesStore from "../../../../stores/ClassesStore";
import ClassMapStore from "../../../../stores/ClassMapStore";
import mapOptions from "./mapOptions";
import "./ClassMap.scss";

const curriculumKeysSort = { sb: 0, lpm: 1, presto: 2 };

const containerStyle = {
  height: "100%",
  width: "100%",
  boxShadow: "var(--shadow)"
};

const ClassMap = ({ center: { lat, lng } = {} }) => {
  const [map, setMap] = useState();
  const [mapBounds, setMapBounds] = useState(null);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const previousBoundsRef = useRef(null);
  const activeBoundsRef = useRef(null);
  const isResizingRef = useRef(false);
  const isProgrammaticRef = useRef(false);

  const { lat: currentLat, lng: currentLng } = ClassMapStore?.currentCenter || {};

  const getSearchBounds = useCallback((latitude, longitude) => {
    const radiusMiles = 10;
    const latDegreeDistance = 69;
    const dLat = radiusMiles / latDegreeDistance;
    const radLat = (latitude * Math.PI) / 180;
    const dLng = radiusMiles / (latDegreeDistance * Math.cos(radLat));
    
    return {
      north: latitude + dLat,
      south: latitude - dLat,
      east: longitude + dLng,
      west: longitude - dLng
    };
  }, []);

  useEffect(() => {
    if (lat != null && lng != null && map) {
      const bounds = getSearchBounds(lat, lng);
      isProgrammaticRef.current = true;
      activeBoundsRef.current = bounds;
      
      ClassMapStore.setCurrentCenter({ lat, lng });
      map.fitBounds(bounds);
    }
  }, [lat, lng, map, getSearchBounds]);

  // Debounce the search to avoid too many API calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(bounds => {
      if (!bounds) return;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      
      if (!ne || !sw) return;

      const boundingBox = {
        north: ne.lat(),
        east: ne.lng(),
        south: sw.lat(),
        west: sw.lng()
      };

      ClassesStore.searchTeachersByMapBounds(boundingBox);
    }, 750),
    []
  );

  // Only trigger search when mapBounds changes and we're not currently interacting
  useEffect(() => {
    if (mapBounds && !isUserInteracting) {
      const boundsChanged = !previousBoundsRef.current || !mapBounds.equals(previousBoundsRef.current);

      if (boundsChanged) {
        previousBoundsRef.current = mapBounds;
        debouncedSearch(mapBounds);
      }
    }
  }, [mapBounds, isUserInteracting, debouncedSearch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFitBounds = useCallback(
    debounce(() => {
      if (map && activeBoundsRef.current) {
        map.fitBounds(activeBoundsRef.current);
        const listener = map.addListener("idle", () => {
          isResizingRef.current = false;
          if (map) {
            const newZoom = map.getZoom();
            const newCenter = map.getCenter();
            if (newZoom != null) {
              ClassMapStore?.setCurrentZoom(newZoom);
            }
            if (newCenter != null) {
              ClassMapStore?.setCurrentCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
            }
            if (map.getBounds()) {
              setMapBounds(map.getBounds());
            }
          }
          listener.remove();
        });
      } else {
        isResizingRef.current = false;
      }
    }, 150),
    [map]
  );

  useEffect(() => {
    if (!map) return;

    const handleResize = () => {
      if (activeBoundsRef.current) {
        isResizingRef.current = true;
        debouncedFitBounds();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      debouncedFitBounds.cancel();
    };
  }, [map, debouncedFitBounds]);

  const { isLoaded } = useJsApiLoader({ id: "google-map-script", googleMapsApiKey: GOOGLE_MAPS_API_KEY });
  if (!isLoaded) return <div />;

  const updateCenter = () => {
    if (isResizingRef.current || isProgrammaticRef.current) return;
    const updatedLat = map?.center?.lat();
    const updatedLng = map?.center?.lng();
    if (updatedLat != null && updatedLng != null && (updatedLat !== currentLat || updatedLng !== currentLng)) {
      ClassMapStore?.setCurrentCenter({ lat: updatedLat, lng: updatedLng });
      if (map && map.getBounds()) {
        setMapBounds(map.getBounds());
      }
    }
  };
  const updateZoom = () => {
    if (isResizingRef.current || isProgrammaticRef.current) return;
    const updatedZoom = map?.zoom;
    if (updatedZoom && updatedZoom !== ClassMapStore?.currentZoom) {
      ClassMapStore?.setCurrentZoom(updatedZoom);

      if (map && map.getBounds()) {
        setMapBounds(map.getBounds());
      }
    }
  };
  const goToTeacherDetailPage = teacherId => () => navigate(`/teachers/${teacherId}?from=map`);

  const hasValidCenterProp = lat != null && lng != null;
  const { center: optCenter, zoom: optZoom, ...restMapOptions } = mapOptions;
  const options = {
    ...restMapOptions,
    isFractionalZoomEnabled: true,
    ...(!map ? {
      center: hasValidCenterProp ? { lat: currentLat || lat, lng: currentLng || lng } : optCenter,
      zoom: ClassMapStore?.currentZoom || optZoom
    } : {})
  };

  const { searchResultsByLocation } = ClassesStore || {};
  const markers = searchResultsByLocation?.map(({ latitude, longitude, teachers }) => {
    const position = { lat: latitude, lng: longitude };

    const curriculaAtThisLocation = Object.entries(
      teachers?.reduce((acc, next) => {
        acc.sb = acc?.sb || next?.canTeachSoundBeginnings;
        acc.lpm = acc?.lpm || next?.canTeachLPM;
        acc.presto = acc?.presto || next?.canTeachPresto;
        return acc;
      }, {})
    )
      ?.filter(([_, isPresent]) => !!isPresent)
      ?.map(([curriculumKey]) => curriculumKey);
    const rawIconKey = Array.from(new Set(curriculaAtThisLocation))
      ?.sort((a, b) => (curriculumKeysSort[a] > curriculumKeysSort[b] ? 1 : -1))
      ?.join("-");
    const iconKey = rawIconKey?.match(/.+-.+-.+/) ? "all" : rawIconKey;

    // TODO: Look at how to handle the multiple teachers in one location edge case
    const teacherId = teachers?.[0]?.id;

    return (
      <Marker
        position={position}
        icon={`https://${ENV}-lpm-assets.b-cdn.net/icons/map-icons/${iconKey}.png`}
        key={latitude + "-" + longitude}
        onClick={goToTeacherDetailPage(teacherId)}
      />
    );
  });

  return (
    <div className="map-wrapper">
      <GoogleMap
        onLoad={mapInstance => {
          setMap(mapInstance);
          if (mapInstance && mapInstance.getBounds()) {
            setMapBounds(mapInstance.getBounds());
            activeBoundsRef.current = mapInstance.getBounds();
          }
        }}
        mapContainerStyle={containerStyle}
        options={options}
        onCenterChanged={updateCenter}
        onZoomChanged={updateZoom}
        onDragStart={() => setIsUserInteracting(true)}
        onDragEnd={() => {
          setIsUserInteracting(false);
          if (map && map.getBounds()) {
            if (!isResizingRef.current && !isProgrammaticRef.current) {
              const bounds = map.getBounds();
              setMapBounds(bounds);
              activeBoundsRef.current = bounds;
            }
          }
        }}
        onZoomStart={() => setIsUserInteracting(true)}
        onIdle={() => {
          setIsUserInteracting(false);
          if (map && map.getBounds()) {
            if (isProgrammaticRef.current) {
              isProgrammaticRef.current = false;
              const bounds = map.getBounds();
              setMapBounds(bounds);
              activeBoundsRef.current = bounds;
              
              const currentZoom = map.getZoom();
              if (currentZoom != null) {
                ClassMapStore.setCurrentZoom(currentZoom);
              }
              return;
            }
            if (!isResizingRef.current) {
              const bounds = map.getBounds();
              setMapBounds(bounds);
              activeBoundsRef.current = bounds;
            }
          }
        }}
      >
        {markers}
      </GoogleMap>
      <div className="map-legend">
        <div className="legend-line">
          <div className="legend-circle sb" />
          <div className="legend-label">Sound Beginnings (ages 0-4)</div>
        </div>
        <div className="legend-line">
          <div className="legend-circle lpm" />
          <div className="legend-label">Let's Play Music (ages 4-6)</div>
        </div>
        <div className="legend-line">
          <div className="legend-circle presto" />
          <div className="legend-label">Presto (ages 7-12)</div>
        </div>
      </div>
    </div>
  );
};

export default observer(ClassMap);
